import * as Switch from '@radix-ui/react-switch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

import { ApiError, violationMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { DEFAULT_COUNTRY_CODE } from '../../lib/constants';
import { ParentShell } from '../components/ParentShell';
import {
  fetchParentProfile,
  learnerNavFromSummary,
  patchParentProfile,
} from '../lib/parent-portal-api';
import { fetchMyLearners, RELATIONSHIP_OPTIONS } from '../lib/registration-api';

const INDIA_DIAL_CODE = '+91';

function digitsOnly(value: FormDataEntryValue | null): string {
  return String(value ?? '').replace(/\D/g, '').slice(0, 10);
}

export function ProfilePage(): ReactElement {
  const queryClient = useQueryClient();
  const { token, loading } = useAuth();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [mobileIsWhatsapp, setMobileIsWhatsapp] = useState(true);

  const learnersQuery = useQuery({
    queryKey: ['my-learners'],
    queryFn: fetchMyLearners,
    enabled: Boolean(token),
  });

  const profileQuery = useQuery({
    queryKey: ['parent-profile'],
    queryFn: fetchParentProfile,
    enabled: Boolean(token),
  });

  const saveMutation = useMutation({
    mutationFn: patchParentProfile,
    onSuccess: async () => {
      setFieldErrors({});
      await queryClient.invalidateQueries({ queryKey: ['parent-profile'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.problem.violations?.length) {
        const next: Record<string, string> = {};
        for (const violation of err.problem.violations) {
          if (violation.field) next[violation.field] = violation.detail ?? violation.code;
        }
        setFieldErrors(next);
      }
    },
  });

  const profile = profileQuery.data;
  const learners = learnerNavFromSummary(learnersQuery.data ?? []);

  useEffect(() => {
    if (!profile) return;
    setMobileIsWhatsapp(
      profile.whatsappNumber == null || profile.whatsappNumber === profile.mobile,
    );
  }, [profile]);

  if (!loading && !token) return <Navigate to="/login" replace />;

  return (
    <ParentShell learners={learners} title="My profile">
      {profileQuery.error ? (
        <div className="error-banner" role="alert">
          {profileQuery.error instanceof ApiError
            ? violationMessage(profileQuery.error.problem)
            : 'Could not load profile'}
        </div>
      ) : null}

      {profileQuery.isLoading || !profile ? <p className="text-muted">Loading…</p> : (
        <form
          className="card profile-form"
          onSubmit={(event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            const mobile = digitsOnly(data.get('mobile'));
            const whatsappNumber = mobileIsWhatsapp
              ? mobile
              : (digitsOnly(data.get('whatsappNumber')) || null);
            saveMutation.mutate({
              firstName: String(data.get('firstName') ?? ''),
              lastName: String(data.get('lastName') ?? ''),
              relationshipToLearner: String(data.get('relationshipToLearner') ?? ''),
              countryCode: DEFAULT_COUNTRY_CODE,
              mobile,
              whatsappCountryCode: whatsappNumber ? DEFAULT_COUNTRY_CODE : null,
              whatsappNumber,
            });
          }}
        >
          <div className="field-row">
            <label className="field">
              <span className="field-label">Email</span>
              <input type="email" value={profile.email ?? ''} disabled />
              <span className="field-hint">This is your sign-in identity and cannot be changed here.</span>
            </label>
            <label className="field">
              <span className="field-label">Relationship to learner</span>
              <select name="relationshipToLearner" defaultValue={profile.relationshipToLearner} required>
                {RELATIONSHIP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="field-row">
            <label className="field">
              <span className="field-label">First name</span>
              <input name="firstName" defaultValue={profile.firstName ?? ''} required />
            </label>
            <label className="field">
              <span className="field-label">Last name</span>
              <input name="lastName" defaultValue={profile.lastName ?? ''} required />
            </label>
          </div>

          <div className="field-row">
            <label className="field">
              <span className="field-label">Mobile</span>
              <div className="phone-input">
                <span className="phone-prefix">{INDIA_DIAL_CODE}</span>
                <input
                  name="mobile"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  defaultValue={profile.mobile ?? ''}
                  aria-label="Mobile number, India +91"
                  onInput={(event) => {
                    event.currentTarget.value = event.currentTarget.value.replace(/\D/g, '').slice(0, 10);
                  }}
                  pattern="[6-9][0-9]{9}"
                  aria-invalid={fieldErrors.mobile ? true : undefined}
                  required
                />
              </div>
              {fieldErrors.mobile ? <span className="field-error">{fieldErrors.mobile}</span> : null}
              <div className="switch-row">
                <label htmlFor="mobileIsWhatsapp">Is this your WhatsApp number?</label>
                <Switch.Root
                  id="mobileIsWhatsapp"
                  className="switch-root"
                  checked={mobileIsWhatsapp}
                  onCheckedChange={(checked) => setMobileIsWhatsapp(checked === true)}
                >
                  <Switch.Thumb className="switch-thumb" />
                </Switch.Root>
              </div>
            </label>
            {mobileIsWhatsapp ? (
              <div className="field" aria-hidden="true" />
            ) : (
              <label className="field">
                <span className="field-label">WhatsApp number</span>
                <div className="phone-input">
                  <span className="phone-prefix">{INDIA_DIAL_CODE}</span>
                  <input
                    name="whatsappNumber"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    defaultValue={profile.whatsappNumber && profile.whatsappNumber !== profile.mobile
                      ? profile.whatsappNumber
                      : ''}
                    aria-label="WhatsApp number, India +91"
                    onInput={(event) => {
                      event.currentTarget.value = event.currentTarget.value.replace(/\D/g, '').slice(0, 10);
                    }}
                    pattern="[6-9][0-9]{9}"
                  />
                </div>
              </label>
            )}
          </div>

          <div className="btn-row">
            <button className="btn" type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      )}
    </ParentShell>
  );
}
